import React, { useState, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import Search from '@xanadu/ui-shared/components/Search';

import { MOBILE_BREAKPOINT, PRODUCTS, PRODUCT_LINKS } from '../../../constants';

import Balances from '../../../containers/header/Balances';
import Buttons from '../../../containers/header/Buttons';

import PopularProducts from '../../homepage/PopularProducts/PopularProducts';
import SlidingProducts from '../SlidingProducts';
import Tile from '../../navigation/MobileNavigation/Tile';

import MBLogo from '../../../../images/logos/mb-logo-light.svg';
import IconAccount from '../../../../images/icons/black/icon-account.svg';

import IconArrowLeft from '../../../../images/icons/red/icon-arrow-left.svg';

import styles from './Header.module.scss';

const getPromotionsDefault = (previousURL = '') => (
  (/^\/casino/.test(previousURL) || previousURL.indexOf('casino') > -1) || (/^\/virtuals/.test(previousURL) || previousURL.indexOf('virtuals') > -1)
    ? '/promotions/casino'
    : '/promotions/exchange'
);

const Header = ({
  location = {},
  history,
  isLoggedIn,
  changeProduct,
  product,
  casinoVisibilityRestricted,
  cinemaMode,
  virtualsAllowed,
  updateRightSidebar,
  toggleNavigation,
  widthSize,
  showLabel,
  setShowLabel,
  sidebarContent,
  storedAccountID
}) => {
  const { pathname } = location;
  const [searchFocused, setSearchFocus] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const accountSidebar = sidebarContent === 'account';

  const isHomepage = pathname === '/';

  const isExchange = product === PRODUCTS.exchange;

  const isMobile = widthSize <= MOBILE_BREAKPOINT;

  const { exchange, vegas, liveVegas } = PRODUCTS;

  const showSearch = [exchange, vegas, liveVegas].includes(product);

  const handleProductSelect = (productUpdate) => {
    changeProduct(productUpdate.id);
    history.push(productUpdate.url);
  };

  const handleAccountPanel = () => {
    setShowLabel(true);
    updateRightSidebar({
      content: 'account',
      visible: true
    });
  };

  const handleBackButton = () => {
    updateRightSidebar({
      visible: false
    });
  };

  const onSearchFocusChange = (searchFocus) => {
    setSearchFocus(searchFocus);
  };

  const filteredProducts = useMemo(() => {
    let newProducts = PRODUCT_LINKS;
    if (casinoVisibilityRestricted) {
      newProducts = newProducts.filter(prod => (
        ![PRODUCTS.casino, PRODUCTS.liveCasino].includes(prod.id)
      ));
    }

    if (!virtualsAllowed) {
      newProducts = newProducts.filter(prod => (
        PRODUCTS.virtuals !== prod.id
      ));
    }

    return newProducts;
  }, [casinoVisibilityRestricted, virtualsAllowed]);

  const containerClass = classNames({
    [styles.container]: true,
    [styles['container--focused']]: searchFocused,
    [styles['container--no-search']]: !showSearch,
    [styles['container--cinemaMode']]: cinemaMode
  });

  const userDataClass = classNames({
    [styles.userData]: true,
    [styles.fadeOut]: expanded,
    [styles.fadeIn]: !expanded
  });

  const subMenuClass = classNames({
    [styles.subMenu]: true,
    [styles['subMenu--no-search']]: !showSearch
  });

  const mainClass = classNames({
    [styles.main]: true,
    [styles['main--mobileAccountPanel']]: accountSidebar
  });

  const logoClass = classNames({
    [styles.logo]: true,
    [styles['logo--leftSide']]: !storedAccountID
  });

  const myAccountClass = (titleContainer) => classNames({
    [styles.myAccount]: true,
    [styles['myAccount--titleContainer']]: titleContainer
  });

  const tabletLinkClass = classNames({
    [styles.tabletLink]: true,
    '.gtm_cta': true,
  });

  return (
    <div className={containerClass}>
      <div className={mainClass}>
        <div className={styles.tabletMenu}>
          <SlidingProducts
            products={filteredProducts}
            changeProduct={changeProduct}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        </div>
        <div className={styles.menu}>
          {filteredProducts.map((filteredProduct) => {
            const isPredictions = filteredProduct.id === PRODUCTS.predictions;
            const isLinkExternal = filteredProduct.url.startsWith('http');
            return (
              <Tile
                key={filteredProduct.id}
                product={filteredProduct.id}
                name={filteredProduct.name}
                iconName={filteredProduct.urlName || PRODUCTS.exchange}
                url={filteredProduct.url}
                onClick={!isPredictions ? () => handleProductSelect(filteredProduct) : null}
                dataHook={`tablet-nav-${filteredProduct.id}`}
                className=".gtm_cta"
                section={'popularProducts'}
                showLabel
                isExternalLink={isLinkExternal}
                openInNewTab={!(isPredictions && isLinkExternal)}
              />
            );
          })}
        </div>
        {!isMobile && (
          <div className={userDataClass}>
            {isLoggedIn ? (
              <div className={styles.loginContainer}>
                <Balances />
              </div>
            ) : (
              <Buttons />
            )}
          </div>
        )}
        {isMobile && (
          accountSidebar ? (
            <div className={styles.accountContainer}>
              <div className={myAccountClass()}>
                <div className={styles.backButton} onClick={handleBackButton} role="button">
                  <IconArrowLeft />
                  Back
                </div>
              </div>
              <div className={myAccountClass(true)}>
                <div className={styles.accountTitle}>
                  My Account
                </div>
              </div>
            </div>
          ) : (
            <Fragment>
              <MBLogo className={logoClass} />
              <div className={userDataClass}>
                {isLoggedIn ? (
                  <div className={styles.loginContainer}>
                    <Balances />
                    <IconAccount className={styles.icon} onClick={handleAccountPanel} />
                  </div>
                ) : (
                  <Buttons />
                )}
              </div>
            </Fragment>
          )
        )}
      </div>
      {
        (isExchange && isMobile && isHomepage && !casinoVisibilityRestricted && virtualsAllowed) && (
          <PopularProducts
            toggleNavigation={toggleNavigation}
            history={history}
            changeProduct={changeProduct}
            showLabel={showLabel}
          />
        )
      }
      <div className={subMenuClass}>
        {showSearch && (
          <Search onInputFocusChange={onSearchFocusChange} />
        )}
        <Link
          to={getPromotionsDefault(pathname)}
          data-hook="promotions-top"
          className=".gtm_cta"
        >
          {window.innerWidth > 800 ? 'Promotions' : 'Promos'}
        </Link>
        <Link
          to="https://insights.matchbook.com"
          target="_blank"
          data-hook="insights-top"
          className=".gtm_cta"
        >
          Insights
        </Link>
        <Link
          to="/page/about/mobile-applications/"
          data-hook="download-apps-top"
          className=".gtm_cta"
        >
          Download Apps
        </Link>
        <Link
          to="https://b2b.matchbook.com/"
          target="_blank"
          data-hook="matchbook-b2b"
          className=".gtm_cta"
        >
          Matchbook B2B
        </Link>
        <Link
          to="/promo/brokerage"
          data-hook="brokerage-top"
          target="_blank"
          rel="noopener noreferrer"
          className={tabletLinkClass}
        >
          <span>Brokerage</span>
          <span>&nbsp;Service</span>
        </Link>
        <Link
          to={isLoggedIn ? '/account/safer-gambling' : '/page/rules_and_regulations/responsible-gambling'}
          data-hook="rg-page"
          className=".gtm_cta"
        >
          Safer Gambling
        </Link>
      </div>
    </div>
  );
};

Header.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }),
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }),
  product: PropTypes.string.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  changeProduct: PropTypes.func.isRequired,
  toggleNavigation: PropTypes.func.isRequired,
  updateRightSidebar: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  casinoVisibilityRestricted: PropTypes.bool.isRequired,
  cinemaMode: PropTypes.bool.isRequired,
  widthSize: PropTypes.number.isRequired,
  showLabel: PropTypes.bool.isRequired,
  sidebarContent: PropTypes.string,
  setShowLabel: PropTypes.func.isRequired,
  storedAccountID: PropTypes.number,
};

export default Header;
